import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import {
  AssociationForm,
  AssociationFormValues,
} from "../../components/association-form";
import { H1 } from "../../components/h1";
import { useGetAssociation } from "../../queries/get-association";
import { updateOwnedAssociation } from "../../services/association";
import { useAuthStore } from "../../stores/auth";
import { useLocale } from "../../stores/locale";

export function ControlAssociationEdit() {
  const { id } = useParams();
  const { locale, currentLanguage } = useLocale();
  const navigate = useNavigate();
  const { isAuth } = useAuthStore();

  if (!id) {
    throw new Error("Association ID is required");
  }

  const queryClient = useQueryClient();
  const { data: association, isLoading: isLoadingAssociation } =
    useGetAssociation(id);

  const [formValues, setFormValues] = useState<AssociationFormValues>({
    name: "",
    domain: "",
    organizationNumber: "",
  });
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const isLoading = isLoadingAssociation || loading;

  useEffect(() => {
    if (association) {
      setFormValues({
        name: association.name,
        domain: association.domain,
        organizationNumber: association.organizationNumber,
      });
    }
  }, [association]);

  if (!isAuth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
      <div className="flex flex-col gap-2">
        <H1>{locale.auth.associationEditTitle}</H1>
        <Link className="text-sm text-blue-700 hover:underline" to="/start">
          {locale.auth.backToAssociationStart}
        </Link>
      </div>

      <AssociationForm
        hasError={hasError}
        loading={isLoading}
        submitLabel={locale.page.save}
        values={formValues}
        onCancel={() => navigate("/admin/users")}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            setHasError(false);
            try {
              await updateOwnedAssociation(
                id,
                {
                  name: formValues.name.trim(),
                  domain: formValues.domain.trim(),
                  organizationNumber: formValues.organizationNumber.trim(),
                },
                currentLanguage,
              );
              await queryClient.invalidateQueries({
                queryKey: ["associations", id],
              });
              navigate("/admin/users");
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </div>
  );
}
